import React, { useMemo } from "react";
import Highcharts from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import { Box, Stack, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import PeriodSelect from "./PeriodSelect";
import WidgetShell from "./WidgetShell";
import { HC_CONTAINER_PROPS } from "./chartTheme";
import { EXD, ExdEmpty, ExdLegendRow } from "./exdMui";

const RING_COLORS = ["#2f5bea", "#f08c1a"];

const SHORT_LABELS = {
  "Current Month Gross Profit": "Current",
  "Prior Month Gross Profit": "Prior",
  "New VINs Added": "New",
  "Old VIN Count": "Existing",
  "Total Completed WOs": "Work orders",
  "Total Units Served": "Units",
  "Total Fleet on Platform": "Fleet",
};

const shorten = (name = "") => {
  if (SHORT_LABELS[name]) return SHORT_LABELS[name];
  const hit = Object.keys(SHORT_LABELS).find((key) =>
    String(name).toLowerCase().includes(key.toLowerCase()),
  );
  return hit ? SHORT_LABELS[hit] : name;
};

const parseCenter = (html, fallbackSub) => {
  if (!html || typeof html !== "string") {
    return { value: "", sub: fallbackSub || "", tone: "" };
  }

  const withBreaks = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<[^>]+>/g, " ");
  const lines = withBreaks
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const primary = lines[0] || "";
  const secondary = lines[1] || fallbackSub || "";

  let tone = "";
  const htmlLower = html.toLowerCase();
  if (
    /[↓⬇]/.test(primary) ||
    htmlLower.includes("color: red") ||
    htmlLower.includes("color:red") ||
    htmlLower.includes("--bad")
  ) {
    tone = "bad";
  } else if (
    /[↑⬆+]/.test(primary) ||
    htmlLower.includes("color: green") ||
    htmlLower.includes("color:green") ||
    htmlLower.includes("--good") ||
    htmlLower.includes("#22c55e") ||
    htmlLower.includes("#16a36a")
  ) {
    tone = "good";
  }

  const value = primary
    .replace(/⬆|↑/g, "↑")
    .replace(/⬇|↓/g, "↓")
    .replace(/\s*%\s*/g, "%")
    .replace(/([↑↓+])\s+/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

  return { value, sub: secondary || fallbackSub || "", tone };
};

const OpsRingWidget = ({
  title,
  infoTitle,
  loading,
  chartOptions,
  periodValue,
  onPeriodChange,
  periodFallback = "All",
  loginUserCurrency,
  money = false,
  centerSub,
}) => {
  const points = useMemo(() => {
    const raw = chartOptions?.series?.[0]?.data;
    if (!Array.isArray(raw) || !raw.length) return [];

    return raw.map((point, index) => {
      const color = RING_COLORS[index % RING_COLORS.length];
      if (point && typeof point === "object" && !Array.isArray(point)) {
        return {
          name: point.name || `Item ${index + 1}`,
          y: Number(point.y) || 0,
          color,
        };
      }
      return {
        name: `Item ${index + 1}`,
        y: Number(point) || 0,
        color,
      };
    });
  }, [chartOptions]);

  const center = useMemo(
    () => parseCenter(chartOptions?.title?.text, centerSub),
    [chartOptions, centerSub],
  );

  const ringOptions = useMemo(() => {
    if (!points.length) return null;

    const hasValue = points.some((p) => p.y > 0);
    const data = hasValue
      ? points.map((p) => ({ name: p.name, y: p.y, color: p.color }))
      : points.map((p) => ({ name: p.name, y: 1, color: "#e3e5ee" }));

    return {
      chart: {
        type: "pie",
        height: 104,
        width: 104,
        backgroundColor: "transparent",
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
        animation: false,
        style: { fontFamily: EXD.font },
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        enabled: hasValue,
        backgroundColor: "#161a3a",
        borderColor: "#161a3a",
        borderRadius: 8,
        outside: true,
        headerFormat: "",
        style: { color: "#fff", fontSize: "12px", fontWeight: "500" },
        pointFormatter() {
          const val = money
            ? nFormatter(this.y, 2)
            : nFormatter(this.y, this.y >= 1000 ? 1 : 0);
          return `<b>${this.name}</b><br/>${val}`;
        },
        useHTML: true,
      },
      plotOptions: {
        pie: {
          innerSize: "72%",
          size: "100%",
          borderWidth: 3,
          borderColor: "#ffffff",
          dataLabels: { enabled: false },
          showInLegend: false,
          allowPointSelect: false,
          enableMouseTracking: hasValue,
          animation: false,
          slicedOffset: 0,
          states: {
            hover: { halo: { size: 0 }, brightness: 0.03 },
          },
        },
      },
      series: [{ type: "pie", name: "Share", data }],
    };
  }, [points, money]);

  const formatValue = (value) => {
    if (money) {
      return (
        <>
          {nFormatter(value, 2)}{" "}
          <CurrencySymbol currency={loginUserCurrency} withParentheses={false} />
        </>
      );
    }
    return Number(value).toLocaleString();
  };

  return (
    <WidgetShell
      title={title}
      infoTitle={infoTitle}
      loading={loading}
      controls={
        onPeriodChange ? (
          <PeriodSelect
            value={periodValue}
            onChange={onPeriodChange}
            fallbackLabel={periodFallback}
          />
        ) : null
      }
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minHeight: 104, width: "100%" }}>
        <Box
          sx={{
            flex: "none",
            height: 104,
            overflow: "visible",
            position: "relative",
            width: 104,
          }}
        >
          {ringOptions ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={ringOptions}
              containerProps={HC_CONTAINER_PROPS}
              immutable
            />
          ) : (
            <Box
              sx={{
                bgcolor: EXD.surface2,
                borderRadius: "50%",
                height: 104,
                width: 104,
              }}
            />
          )}
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              inset: 0,
              pointerEvents: "none",
              position: "absolute",
              textAlign: "center",
            }}
          >
            {center.value ? (
              <Typography
                component="b"
                sx={{
                  color:
                    center.tone === "good"
                      ? EXD.good
                      : center.tone === "bad"
                        ? EXD.bad
                        : EXD.text,
                  fontFamily: EXD.font,
                  fontSize: 15,
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {center.value}
              </Typography>
            ) : null}
            {center.sub ? (
              <Typography sx={{ color: EXD.muted, fontSize: 10.5, fontWeight: 600 }}>
                {center.sub}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          {points.length ? (
            points.map((point, index) => (
              <ExdLegendRow
                key={`${point.name}-${index}`}
                color={point.color}
                label={shorten(point.name)}
                value={formatValue(point.y)}
              />
            ))
          ) : (
            <ExdEmpty />
          )}
        </Stack>
      </Stack>
    </WidgetShell>
  );
};

export default OpsRingWidget;
